import React, { useEffect, useRef, useState } from "react";
import {
    Send, ExternalLink, CheckCircle2, Loader2, Link2Off, Bell, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import ConfirmModal from "./ConfirmModal";

/**
 * TelegramConnectCard — handles per-user @AutoAds_AI_bot binding (PR #13).
 *
 * States:
 *   • loading       — initial /telegram/status fetch
 *   • not_connected — shows Connect CTA
 *   • connecting    — deep-link issued, modal open, polling every 3s
 *   • connected     — shows @username + Test/Disconnect actions
 */
const TelegramConnectCard = ({ onChange }) => {
    const [status, setStatus] = useState({ loading: true });
    const [connecting, setConnecting] = useState(false);
    const [deepLink, setDeepLink] = useState(null);
    const [testing, setTesting] = useState(false);
    const [confirmDisc, setConfirmDisc] = useState(false);
    const [disconnecting, setDisconnecting] = useState(false);
    const pollRef = useRef(null);

    const fetchStatus = async () => {
        try {
            const r = await api.get("/telegram/status");
            setStatus({ ...r.data, loading: false });
            return r.data;
        } catch {
            setStatus({ connected: false, loading: false });
            return null;
        }
    };

    useEffect(() => {
        fetchStatus();
        return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }, []);

    // Poll while connect modal is open
    useEffect(() => {
        if (!connecting) return;
        pollRef.current = setInterval(async () => {
            const s = await fetchStatus();
            if (s?.connected) {
                clearInterval(pollRef.current);
                setConnecting(false);
                setDeepLink(null);
                toast.success("Telegram успішно підключено 🎉");
                onChange?.();
            }
        }, 3000);
        return () => clearInterval(pollRef.current);
    }, [connecting]);

    const startConnect = async () => {
        try {
            const r = await api.post("/telegram/connect/start");
            setDeepLink(r.data.deep_link);
            setConnecting(true);
            // Open Telegram immediately in a new tab
            window.open(r.data.deep_link, "_blank", "noopener,noreferrer");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Не вдалося згенерувати посилання");
        }
    };

    const handleTest = async () => {
        setTesting(true);
        try {
            const r = await api.post("/telegram/test");
            const sent = (r.data.samples || []).filter(s => s.telegram === "sent").length;
            toast.success(`Надіслано ${sent}/3 тестових повідомлень. Перевірте Telegram.`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Не вдалося надіслати тест");
        } finally {
            setTesting(false);
        }
    };

    const handleDisconnect = async () => {
        setDisconnecting(true);
        try {
            await api.post("/telegram/disconnect");
            await fetchStatus();
            toast.success("Telegram відключено");
            setConfirmDisc(false);
            onChange?.();
        } catch (e) {
            toast.error("Не вдалося відключити");
        } finally {
            setDisconnecting(false);
        }
    };

    if (status.loading) {
        return (
            <div className="flex items-center gap-2 text-sm text-faint-c py-3"
                 data-testid="telegram-card-loading">
                <Loader2 size={14} className="animate-spin" />
                Перевіряю стан Telegram…
            </div>
        );
    }

    // ── CONNECTED ─────────────────────────────────────────────────
    if (status.connected) {
        return (
            <div className="rounded-xl border border-success/40 bg-success/5 p-4"
                 data-testid="telegram-card-connected">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-success/15 flex items-center justify-center shrink-0">
                        <Send size={18} className="text-success-c" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                            <span className="font-display font-semibold text-text-primary">Telegram підключено</span>
                            <CheckCircle2 size={14} className="text-success-c" />
                        </div>
                        <div className="mt-1 text-xs text-secondary-c">
                            {status.tg_username
                                ? <>Чат: <span className="font-mono">@{status.tg_username}</span></>
                                : status.tg_first_name
                                    ? <>Імʼя: <span className="font-mono">{status.tg_first_name}</span></>
                                    : "Активний звʼязок"}
                        </div>
                    </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={handleTest}
                        disabled={testing}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md
                                   bg-surface-hover/40 hover:bg-surface-hover/60 border border-border
                                   text-text-primary text-xs font-semibold transition-colors
                                   disabled:opacity-60 disabled:cursor-wait"
                        data-testid="telegram-test"
                    >
                        {testing
                            ? <Loader2 size={12} className="animate-spin" />
                            : <Bell size={12} />}
                        <span>Надіслати тестові повідомлення</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => setConfirmDisc(true)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md
                                   border border-error/30 text-error-c
                                   hover:bg-error/10 text-xs font-semibold transition-colors"
                        data-testid="telegram-disconnect"
                    >
                        <Link2Off size={12} />
                        <span>Відключити</span>
                    </button>
                </div>

                <ConfirmModal
                    open={confirmDisc}
                    onClose={() => setConfirmDisc(false)}
                    onConfirm={handleDisconnect}
                    loading={disconnecting}
                    danger
                    title="Відключити Telegram?"
                    body="Сповіщення в Telegram-боті @AutoAds_AI_bot припиняться. Інші канали (in-app, email) залишаться як налаштовано."
                    confirmLabel="Так, відключити"
                    cancelLabel="Залишити"
                    testidPrefix="telegram-disconnect-modal"
                />
            </div>
        );
    }

    // ── NOT CONNECTED ─────────────────────────────────────────────
    return (
        <div className="rounded-xl border border-border bg-surface-hover/10 p-4"
             data-testid="telegram-card-not-connected">
            <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <Send size={18} className="text-primary-c" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-text-primary">
                        Telegram-сповіщення
                    </div>
                    <p className="mt-1 text-xs text-secondary-c leading-relaxed">
                        Підключіть бот <span className="font-mono">@AutoAds_AI_bot</span>, щоб отримувати критичні алерти
                        й готові звіти напряму в Telegram. Звʼязати акаунт займає 5 секунд.
                    </p>
                </div>
            </div>

            <button
                type="button"
                onClick={startConnect}
                className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-md
                           bg-primary hover:bg-primary/90 text-white text-xs font-semibold transition-colors"
                data-testid="telegram-connect"
            >
                <ExternalLink size={12} />
                <span>Підключити Telegram</span>
            </button>

            {/* Connecting modal */}
            {connecting && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
                    onClick={() => { setConnecting(false); setDeepLink(null); }}
                    data-testid="telegram-connecting-overlay"
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="glass rounded-xl border border-border p-6 max-w-md w-full"
                    >
                        <div className="flex items-center gap-2 mb-2">
                            <Loader2 size={16} className="animate-spin text-primary-c" />
                            <span className="font-display font-semibold text-text-primary">Очікую підтвердження…</span>
                        </div>
                        <p className="text-sm text-secondary-c leading-relaxed">
                            Відкрийте Telegram і натисніть <strong>Start</strong> у боті{" "}
                            <span className="font-mono">@AutoAds_AI_bot</span>.
                        </p>
                        <p className="mt-3 text-xs text-faint-c leading-relaxed">
                            Якщо вкладка не відкрилась автоматично —
                            {deepLink && (
                                <> <a href={deepLink} target="_blank" rel="noopener noreferrer"
                                      className="text-primary-c underline">натисніть сюди</a>.</>
                            )}
                        </p>
                        <div className="mt-3 flex items-start gap-2 p-3 rounded-md bg-warning/10 border border-warning/30">
                            <AlertCircle size={12} className="text-warning-c shrink-0 mt-0.5" />
                            <span className="text-[11px] text-secondary-c leading-relaxed">
                                Посилання діє 15 хвилин і використовується одноразово.
                            </span>
                        </div>
                        <button
                            type="button"
                            onClick={() => { setConnecting(false); setDeepLink(null); }}
                            className="mt-4 w-full px-3 py-2 rounded-md bg-surface-hover/40 hover:bg-surface-hover/60
                                       border border-border text-text-primary text-xs font-semibold transition-colors"
                            data-testid="telegram-connecting-cancel"
                        >
                            Скасувати
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TelegramConnectCard;
